import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Eye, EyeOff, KeyRound, Monitor } from 'lucide-react';
import { AvatarInitials } from '@/components/ui/AvatarInitials';
import { ptBR } from '@/i18n/pt-BR';
import { cn } from '@/lib/utils';
import { useApiClient } from '@/lib/api';
import { getPerfil, PerfilError, updatePerfil, type PerfilData } from '@/lib/perfil';
import { roleLabel } from '@/lib/roles';
import { Button, Input, buttonVariants } from '@/components/ui';

const DENSITY_STORAGE_KEY = 'signcheck.preferences.tableDensity';
type TableDensity = 'compact' | 'comfortable';

function mapPerfilErrorToMessage(err: unknown): string {
  if (err instanceof PerfilError) {
    if (err.reason === 'network') return ptBR.errors.networkOffline;
    if (err.reason === 'validation') return ptBR.errors.validation;
  }
  return ptBR.errors.generic;
}

function readInitialDensity(): TableDensity {
  if (typeof window === 'undefined') return 'comfortable';
  const stored = window.localStorage.getItem(DENSITY_STORAGE_KEY);
  return stored === 'compact' ? 'compact' : 'comfortable';
}

interface CampoPessoalSectionProps {
  /** Prefixo dos `data-testid` e do `id` do título (ex.: `perfil-registro`). */
  testIdBase: string;
  titulo: string;
  hint: string;
  label: string;
  placeholder: string;
  valorInicial: string | null;
  ctaSalvar: string;
  mensagemSalvo: string;
  mensagemErro: string;
  /** Envia o PATCH e devolve o valor JÁ normalizado pelo servidor. */
  salvar: (valor: string) => Promise<string | null>;
}

/**
 * Campo de cadastro que a PRÓPRIA pessoa edita (E52.F3; generalizado na E56.F1).
 *
 * Dois consumidores hoje: o registro profissional, que o laudo certificado
 * imprime quando ela assina (ADR-0063 §5), e o nome de exibição, que o shell
 * mostra. Nos dois, campo em branco significa "não cadastrado" — o servidor
 * normaliza, e é por isso que o valor exibido depois de salvar é o que ELE
 * devolveu, não o que foi digitado.
 */
function CampoPessoalSection(props: CampoPessoalSectionProps) {
  const { testIdBase, titulo, hint, label, placeholder, valorInicial } = props;
  const { ctaSalvar, mensagemSalvo, mensagemErro, salvar } = props;
  const [valor, setValor] = useState(valorInicial ?? '');
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function handleSalvar() {
    setSalvando(true);
    setErro(null);
    setSalvo(false);
    try {
      setValor((await salvar(valor)) ?? '');
      setSalvo(true);
    } catch {
      setErro(mensagemErro);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="mt-10" aria-labelledby={`${testIdBase}-title`} data-testid={testIdBase}>
      <h2 id={`${testIdBase}-title`} className="text-heading-md text-fg">
        {titulo}
      </h2>
      <p className="mt-2 text-body-sm text-fg-muted">{hint}</p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-body-sm text-fg-muted">
          <span>{label}</span>
          <Input
            type="text"
            maxLength={120}
            value={valor}
            disabled={salvando}
            placeholder={placeholder}
            data-testid={`${testIdBase}-input`}
            onChange={(e) => {
              setValor(e.target.value);
              setSalvo(false);
            }}
          />
        </label>
        <Button
          variant="secondary"
          type="button"
          onClick={() => void handleSalvar()}
          disabled={salvando}
          data-testid={`${testIdBase}-save`}
        >
          {salvando ? ptBR.perfil.registroSaving : ctaSalvar}
        </Button>
      </div>
      {salvo ? (
        <p
          role="status"
          data-testid={`${testIdBase}-saved`}
          className="mt-2 text-caption text-success-700"
        >
          {mensagemSalvo}
        </p>
      ) : null}
      {erro !== null ? (
        <p
          role="alert"
          data-testid={`${testIdBase}-error`}
          className="mt-2 text-caption text-danger-700"
        >
          {erro}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Cabeçalho da conta: nome quando cadastrado, e-mail sempre presente.
 *
 * E56.F1.T04: mesma normalização da Sidebar — `"  "` não é nome, é ausência —
 * e o e-mail nunca some da tela, só perde a ênfase tipográfica quando há nome.
 */
function PerfilHeader({ perfil }: { perfil: PerfilData }) {
  const nome = perfil.display_name?.trim() || null;
  return (
    <header className="mt-6 flex items-center gap-4">
      <AvatarInitials source={nome ?? perfil.email} size={56} data-testid="perfil-avatar" />
      <div className="flex flex-col">
        {nome !== null ? (
          <span className="text-heading-md text-fg" data-testid="perfil-nome-exibido">
            {nome}
          </span>
        ) : null}
        <span
          className={nome !== null ? 'text-body-sm text-fg-muted' : 'text-heading-md text-fg'}
          data-testid="perfil-email"
        >
          {perfil.email}
        </span>
        {/* F-E52.F1-05: o rótulo sai do helper único de papéis — a comparação
            local só conhecia `owner` e mostrava "Usuário" a um Certificador,
            que é justamente o papel que assina laudo. */}
        <span
          className="text-caption uppercase tracking-wide text-fg-muted"
          data-testid="perfil-role"
        >
          {roleLabel(perfil.role)}
        </span>
      </div>
    </header>
  );
}

function PerfilContent({
  perfil,
  onPerfilAtualizado,
}: {
  perfil: PerfilData;
  onPerfilAtualizado: (perfil: PerfilData) => void;
}) {
  const apiClient = useApiClient();
  const [showTechId, setShowTechId] = useState(false);
  const [copied, setCopied] = useState(false);
  const [density, setDensity] = useState<TableDensity>(readInitialDensity);

  const handleCopy = useCallback(async () => {
    if (perfil.escritorio_id === null) return;
    try {
      await navigator.clipboard.writeText(perfil.escritorio_id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      setCopied(false);
    }
  }, [perfil.escritorio_id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(DENSITY_STORAGE_KEY, density);
  }, [density]);

  return (
    <>
      <PerfilHeader perfil={perfil} />

      <dl className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-[14rem_1fr]">
        <dt className="text-body-sm text-fg-muted">{ptBR.perfil.escritorioLabel}</dt>
        <dd className="text-body-md text-fg">
          {perfil.escritorio_id !== null ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="link"
                onClick={() => setShowTechId((s) => !s)}
                data-testid="perfil-tech-id-toggle"
                aria-pressed={showTechId}
                className="gap-1.5 text-body-sm"
              >
                {showTechId ? (
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
                <span>{showTechId ? ptBR.perfil.techIdHide : ptBR.perfil.techIdShow}</span>
              </Button>
              {showTechId ? (
                <span
                  className="font-mono text-caption text-fg-muted"
                  data-testid="perfil-escritorio-id"
                >
                  {perfil.escritorio_id}
                </span>
              ) : (
                <span data-testid="perfil-escritorio-id" className="sr-only">
                  {perfil.escritorio_id}
                </span>
              )}
              {showTechId ? (
                <Button
                  variant="secondary"
                  type="button"
                  onClick={handleCopy}
                  data-testid="perfil-tech-id-copy"
                >
                  <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{ptBR.perfil.techIdCopy}</span>
                </Button>
              ) : null}
              {copied ? (
                <span
                  role="status"
                  data-testid="perfil-tech-id-copied"
                  className="text-caption text-success-700"
                >
                  {ptBR.perfil.techIdCopied}
                </span>
              ) : null}
            </div>
          ) : (
            <span data-testid="perfil-escritorio-id">—</span>
          )}
        </dd>
      </dl>

      {/* E56.F1.T04: o nome vem primeiro — é o que a pessoa vê no menu, e sem
          um caminho de cadastro o shell continuaria caindo no e-mail. */}
      <CampoPessoalSection
        testIdBase="perfil-nome"
        titulo={ptBR.perfil.nomeSectionTitle}
        hint={ptBR.perfil.nomeSectionHint}
        label={ptBR.perfil.nomeLabel}
        placeholder={ptBR.perfil.nomePlaceholder}
        valorInicial={perfil.display_name}
        ctaSalvar={ptBR.perfil.nomeSave}
        mensagemSalvo={ptBR.perfil.nomeSaved}
        mensagemErro={ptBR.perfil.nomeError}
        salvar={async (valor) => {
          // Só o campo que mudou vai no corpo: o PATCH é parcial no servidor, e
          // mandar o registro junto o sobrescreveria com o estado da tela.
          const atualizado = await updatePerfil(apiClient, { display_name: valor });
          onPerfilAtualizado(atualizado);
          return atualizado.display_name;
        }}
      />

      <CampoPessoalSection
        testIdBase="perfil-registro"
        titulo={ptBR.perfil.registroSectionTitle}
        hint={ptBR.perfil.registroSectionHint}
        label={ptBR.perfil.registroLabel}
        placeholder={ptBR.perfil.registroPlaceholder}
        valorInicial={perfil.registro_profissional}
        ctaSalvar={ptBR.perfil.registroSave}
        mensagemSalvo={ptBR.perfil.registroSaved}
        mensagemErro={ptBR.perfil.registroError}
        salvar={async (valor) => {
          const atualizado = await updatePerfil(apiClient, { registro_profissional: valor });
          onPerfilAtualizado(atualizado);
          return atualizado.registro_profissional;
        }}
      />

      {/* E164.F2 (ADR-0113): a assinatura com certificado digital confere este CPF; só a
          plataforma assina laudo. */}
      {perfil.is_platform ? (
        <CampoPessoalSection
          testIdBase="perfil-cpf"
          titulo={ptBR.perfil.cpfSectionTitle}
          hint={ptBR.perfil.cpfSectionHint}
          label={ptBR.perfil.cpfLabel}
          placeholder={ptBR.perfil.cpfPlaceholder}
          valorInicial={perfil.cpf ?? null}
          ctaSalvar={ptBR.perfil.cpfSave}
          mensagemSalvo={ptBR.perfil.cpfSaved}
          mensagemErro={ptBR.perfil.cpfError}
          salvar={async (valor) => {
            const atualizado = await updatePerfil(apiClient, { cpf: valor });
            onPerfilAtualizado(atualizado);
            return atualizado.cpf ?? null;
          }}
        />
      ) : null}

      <section className="mt-10" aria-labelledby="api-keys-section-title">
        <h2 id="api-keys-section-title" className="text-heading-md text-fg">
          {ptBR.perfil.apiKeysSection}
        </h2>
        <p className="mt-2 text-body-sm text-fg-muted">{ptBR.perfil.apiKeysSectionHint}</p>
        <Link
          to="/configuracoes/api-keys"
          className={cn(
            buttonVariants({ variant: 'secondary', size: 'sm' }),
            'mt-sc-4 gap-sc-2 no-underline',
          )}
          data-testid="perfil-link-api-keys"
        >
          <KeyRound className="h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
          {ptBR.perfil.apiKeysManageLink}
        </Link>
      </section>

      <section
        className="mt-10"
        aria-labelledby="perfil-preferences-title"
        data-testid="perfil-preferences"
      >
        <h2 id="perfil-preferences-title" className="text-heading-md text-fg">
          {ptBR.perfil.preferencesSectionTitle}
        </h2>
        <p className="mt-2 text-body-sm text-fg-muted">{ptBR.perfil.preferencesSectionHint}</p>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-[14rem_1fr]">
          <span className="flex items-center gap-2 text-body-sm text-fg">
            <Monitor className="h-4 w-4 text-fg-muted" aria-hidden="true" />
            {ptBR.perfil.preferencesTimezoneLabel}
          </span>
          <span className="text-body-md text-fg-muted" data-testid="perfil-timezone">
            {ptBR.perfil.preferencesTimezoneAuto}
          </span>

          <span className="text-body-sm text-fg">{ptBR.perfil.preferencesDensityLabel}</span>
          <div
            className="flex gap-3"
            role="radiogroup"
            aria-label={ptBR.perfil.preferencesDensityLabel}
          >
            <label className="flex items-center gap-2 text-body-sm text-fg">
              <input
                type="radio"
                name="density"
                value="comfortable"
                checked={density === 'comfortable'}
                onChange={() => setDensity('comfortable')}
                data-testid="perfil-density-comfortable"
              />
              {ptBR.perfil.preferencesDensityComfortable}
            </label>
            <label className="flex items-center gap-2 text-body-sm text-fg">
              <input
                type="radio"
                name="density"
                value="compact"
                checked={density === 'compact'}
                onChange={() => setDensity('compact')}
                data-testid="perfil-density-compact"
              />
              {ptBR.perfil.preferencesDensityCompact}
            </label>
          </div>
        </div>
      </section>
    </>
  );
}

export function PerfilPage() {
  const apiClient = useApiClient();

  const [perfil, setPerfil] = useState<PerfilData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getPerfil(apiClient)
      .then((data) => {
        if (!cancelled) setPerfil(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(mapPerfilErrorToMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  return (
    <section data-testid="perfil-page" aria-labelledby="perfil-title" className="mx-auto max-w-3xl">
      <p className="mb-1.5 font-titulo text-rotulo uppercase text-fg-dim">{ptBR.perfil.eyebrow}</p>
      <h1 id="perfil-title" className="text-display-md text-fg">
        {ptBR.perfil.pageTitle}
      </h1>
      <p className="mt-2 text-body-md text-fg-muted">{ptBR.perfil.intro}</p>

      {loading ? (
        <p className="mt-8 text-body-md text-fg-muted" data-testid="perfil-loading">
          {ptBR.common.loading}
        </p>
      ) : null}
      {!loading && loadError ? (
        <p
          className="mt-8 border border-danger bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
          role="alert"
          data-testid="perfil-load-error"
        >
          {loadError}
        </p>
      ) : null}
      {!loading && !loadError && perfil ? (
        <PerfilContent perfil={perfil} onPerfilAtualizado={setPerfil} />
      ) : null}
    </section>
  );
}
